import { useEffect, useMemo, useState } from 'react'
import { CheckCircle2, Copy, QrCode, RefreshCw, Smartphone, WifiOff } from 'lucide-react'
import { toast } from 'sonner'

import { Spinner } from '@/components/common/Spinner'
import { Button } from '@/components/common/Button'
import InstancesService from '@/services/instances.service'
import { useInstancesStore } from '@/stores/instancesStore'
import { QR_REFRESH_INTERVAL } from '@/utils/app-constants'
import type { Instance } from '@/types'

interface QRCodeDisplayProps {
  instanceId: string
  initialStatus: Instance['status']
  initialQRData?: string
  initialPublicQrUrl?: string | null
  initialRemoteInstanceId?: string | null
  forcePrepareFlow?: boolean
}

const STATUS_LABELS: Record<Instance['status'], string> = {
  connected: 'Conectado',
  connecting: 'Conectando',
  qr_ready: 'Aguardando leitura do QR',
  disconnected: 'Desconectado',
}

const INITIAL_QR_DELAY_SECONDS = 5

export function QRCodeDisplay({
  instanceId,
  initialStatus,
  initialQRData,
  initialPublicQrUrl,
  initialRemoteInstanceId,
  forcePrepareFlow = false,
}: QRCodeDisplayProps) {
  const updateInstance = useInstancesStore((state) => state.updateInstance)
  const hasInitialQr = Boolean(initialQRData)
  const canOpenExistingQr =
    hasInitialQr || initialStatus === 'qr_ready' || initialStatus === 'connected'
  const [status, setStatus] = useState<Instance['status']>(initialStatus)
  const [qrData, setQRData] = useState<string | undefined>(
    initialQRData
      ? initialQRData.startsWith('data:')
        ? initialQRData
        : `data:image/png;base64,${initialQRData}`
      : undefined,
  )
  const [publicQrUrl, setPublicQrUrl] = useState<string | null | undefined>(
    initialPublicQrUrl,
  )
  const [remoteInstanceId, setRemoteInstanceId] = useState<string | null | undefined>(
    initialRemoteInstanceId,
  )
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [countdown, setCountdown] = useState(QR_REFRESH_INTERVAL / 1000)
  const [prepareCountdown, setPrepareCountdown] = useState(INITIAL_QR_DELAY_SECONDS)
  const [isPreparingQr, setIsPreparingQr] = useState(forcePrepareFlow && !canOpenExistingQr)
  const [hasRequestedQr, setHasRequestedQr] = useState(canOpenExistingQr)
  const [loadError, setLoadError] = useState<string | null>(null)

  const statusLabel = useMemo(() => STATUS_LABELS[status], [status])

  async function syncStatus() {
    try {
      const result = await InstancesService.getStatus(instanceId)
      setStatus((currentStatus) => {
        if (currentStatus === 'connected' && result.status !== 'connected' && !result.qr_code_data) {
          return currentStatus
        }
        return result.status
      })
      setPublicQrUrl(result.public_qr_url)
      setRemoteInstanceId(result.remote_instance_id)
      if (result.qr_code_data) {
        setQRData(result.qr_code_data)
      }
      updateInstance(instanceId, {
        status: result.status,
        phone_number: result.phone_number ?? undefined,
        jid: result.jid ?? undefined,
        profile_picture_url: result.profile_picture_url ?? undefined,
        qr_code_data: result.qr_code_data,
        public_qr_url: result.public_qr_url,
        remote_instance_id: result.remote_instance_id,
        last_connection_at: result.connected_at ?? undefined,
      })
    } catch {
      // Keep the current state; the modal can still show the cached QR.
    }
  }

  async function refreshQR({ silent = false }: { silent?: boolean } = {}) {
    if (isRefreshing) return
    setIsRefreshing(true)
    setLoadError(null)
    setIsPreparingQr(false)
    setHasRequestedQr(true)
    try {
      const result = await InstancesService.getQRCode(instanceId)
      setStatus(result.status)
      setPublicQrUrl(result.public_qr_url)
      setRemoteInstanceId(result.remote_instance_id)
      setQRData(result.qr_code_data)
      setCountdown(QR_REFRESH_INTERVAL / 1000)

      updateInstance(instanceId, {
        status: result.status,
        profile_picture_url: result.profile_picture_url ?? undefined,
        qr_code_data: result.qr_code_data,
        public_qr_url: result.public_qr_url,
        remote_instance_id: result.remote_instance_id,
      })

      if (!result.qr_code_data && result.status !== 'connected') {
        setLoadError('O QR Code ainda não ficou pronto. Aguarde alguns segundos e tente novamente.')
      }
    } catch {
      setLoadError('Não foi possível obter o QR Code da instância.')
      if (!silent) {
        toast.error('Não foi possível obter o QR Code da instância.')
      }
    } finally {
      setIsRefreshing(false)
    }
  }

  async function copyPublicLink() {
    if (!publicQrUrl) {
      toast.error('Ative o link público para copiar a URL.')
      return
    }

    try {
      await navigator.clipboard.writeText(publicQrUrl)
      toast.success('Link público copiado.')
    } catch {
      toast.error('Não foi possível copiar o link público.')
    }
  }

  useEffect(() => {
    if (canOpenExistingQr) {
      setIsPreparingQr(false)
      setHasRequestedQr(true)
      setLoadError(null)
      if (!hasInitialQr && initialStatus === 'qr_ready') {
        refreshQR({ silent: true }).catch(() => undefined)
      }
      return
    }

    setLoadError(null)
    setIsPreparingQr(true)
    setHasRequestedQr(false)
    setPrepareCountdown(INITIAL_QR_DELAY_SECONDS)
    setCountdown(QR_REFRESH_INTERVAL / 1000)
    setStatus(initialStatus === 'disconnected' ? 'connecting' : initialStatus)
    setPublicQrUrl(initialPublicQrUrl)
    setRemoteInstanceId(initialRemoteInstanceId)
    setQRData(undefined)

    let cancelled = false
    const countdownInterval = setInterval(() => {
      setPrepareCountdown((current) => {
        if (current <= 1) {
          return 0
        }
        return current - 1
      })
    }, 1000)

    const timerId = setTimeout(() => {
      if (cancelled) return
      refreshQR({ silent: true }).catch(() => undefined)
    }, INITIAL_QR_DELAY_SECONDS * 1000)

    return () => {
      cancelled = true
      clearInterval(countdownInterval)
      clearTimeout(timerId)
    }
  }, [
    canOpenExistingQr,
    forcePrepareFlow,
    hasInitialQr,
    initialPublicQrUrl,
    initialRemoteInstanceId,
    initialStatus,
    instanceId,
  ])

  useEffect(() => {
    if (!hasRequestedQr || status === 'connected') return
    const statusInterval = setInterval(() => {
      syncStatus().catch(() => undefined)
    }, 5000)

    return () => clearInterval(statusInterval)
  }, [hasRequestedQr, instanceId, status])

  useEffect(() => {
    if (!hasRequestedQr || status !== 'qr_ready') return

    const intervalId = setInterval(() => {
      refreshQR().catch(() => undefined)
    }, QR_REFRESH_INTERVAL)

    return () => clearInterval(intervalId)
  }, [hasRequestedQr, instanceId, status])

  useEffect(() => {
    if (!hasRequestedQr || status !== 'qr_ready') return

    const timerId = setInterval(() => {
      setCountdown((current) => {
        if (current <= 1) {
          return QR_REFRESH_INTERVAL / 1000
        }
        return current - 1
      })
    }, 1000)

    return () => clearInterval(timerId)
  }, [hasRequestedQr, status])

  if (isPreparingQr) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col gap-3 rounded-[var(--radius-xl)] border border-[var(--border-subtle)] bg-[rgba(255,255,255,0.03)] p-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.24em] text-[var(--text-muted)]">
              Preparando conexão
            </p>
            <h3 className="mt-2 text-2xl font-semibold text-[var(--text-primary)]">
              Gerando o QR Code
            </h3>
          </div>
          <div className="inline-flex items-center gap-2 rounded-full border border-[rgba(245,158,11,0.22)] bg-[rgba(245,158,11,0.12)] px-3 py-1 text-sm font-medium text-[var(--color-warning)]">
            <span className="status-dot status-dot-connecting" />
            Aguarde {prepareCountdown}s
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
          <div className="rounded-[28px] border border-[rgba(255,255,255,0.06)] bg-[linear-gradient(160deg,rgba(255,255,255,0.08),rgba(var(--color-brand-rgb),0.08))] p-5 shadow-[0_20px_60px_rgba(8,10,34,0.35)]">
            <div className="flex min-h-[360px] flex-col items-center justify-center rounded-[24px] bg-white px-6 py-10 text-center shadow-[0_18px_40px_rgba(15,23,42,0.24)]">
              <Spinner size="lg" label="Solicitando QR Code na Pastorini" />
              <p className="mt-6 max-w-sm text-sm text-slate-500">
                A instância foi criada e o sistema vai consultar o QR automaticamente após a janela inicial de estabilização.
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="rounded-[var(--radius-xl)] border border-[var(--border-subtle)] bg-[var(--glass-light)] p-4">
              <p className="text-xs uppercase tracking-[0.22em] text-[var(--text-muted)]">
                Fluxo de criação
              </p>
              <ol className="mt-3 space-y-3 text-sm text-[var(--text-secondary)]">
                <li className="flex gap-3">
                  <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[rgba(var(--color-brand-rgb),0.12)] text-xs font-semibold text-[var(--color-brand)]">1</span>
                  A instância foi criada no gateway e estamos aguardando a janela inicial de 5 segundos.
                </li>
                <li className="flex gap-3">
                  <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[rgba(var(--color-brand-rgb),0.12)] text-xs font-semibold text-[var(--color-brand)]">2</span>
                  Depois dessa espera, o sistema chama <strong className="text-[var(--text-primary)]">GET /api/instances/{'{instance}'}/qr</strong>.
                </li>
                <li className="flex gap-3">
                  <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[rgba(var(--color-brand-rgb),0.12)] text-xs font-semibold text-[var(--color-brand)]">3</span>
                  Assim que o QR chegar, ele aparece nesta janela para o usuário escanear.
                </li>
              </ol>
            </div>

            <div className="grid gap-3 rounded-[var(--radius-xl)] border border-[var(--border-subtle)] bg-[var(--glass-light)] p-4">
              <div className="flex items-center justify-between text-sm">
                <span className="inline-flex items-center gap-2 text-[var(--text-muted)]">
                  <Smartphone size={14} />
                  ID da conexão
                </span>
                <span className="font-medium text-[var(--text-primary)]">
                  {remoteInstanceId ?? 'Gerando...'}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-[var(--text-muted)]">Próxima consulta do QR</span>
                <span className="font-medium text-[var(--text-primary)]">
                  {prepareCountdown}s
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (status === 'connected') {
    return (
      <div className="space-y-6">
        <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-[rgba(16,185,129,0.14)] text-[var(--color-success)] shadow-[0_0_24px_rgba(16,185,129,0.2)]">
          <CheckCircle2 size={42} />
        </div>

        <div className="space-y-2 text-center">
          <h3 className="text-2xl font-semibold text-[var(--text-primary)]">
            Instância conectada
          </h3>
          <p className="mx-auto max-w-md text-sm text-[var(--text-secondary)]">
            O WhatsApp já foi pareado e a conexão está pronta para uso.
          </p>
        </div>

        <div className="grid gap-3 rounded-[var(--radius-xl)] border border-[var(--border-subtle)] bg-[var(--glass-light)] p-4">
          <div className="flex items-center justify-between text-sm">
            <span className="text-[var(--text-muted)]">ID da conexão</span>
            <span className="font-medium text-[var(--text-primary)]">
              {remoteInstanceId ?? 'Não informado'}
            </span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-[var(--text-muted)]">Status</span>
            <span className="font-medium text-[var(--color-success)]">Conectado</span>
          </div>
        </div>
      </div>
    )
  }

  if ((status === 'disconnected' && !qrData) || (loadError && !qrData)) {
    return (
      <div className="space-y-6 py-4">
        <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-[rgba(239,68,68,0.14)] text-[var(--color-error)]">
          <WifiOff size={42} />
        </div>

        <div className="space-y-2 text-center">
          <h3 className="text-2xl font-semibold text-[var(--text-primary)]">
            QR Code indisponível
          </h3>
          <p className="mx-auto max-w-md text-sm text-[var(--text-secondary)]">
            {loadError ?? 'Gere um novo QR Code para parear novamente essa instância.'}
          </p>
        </div>

        <div className="flex justify-center">
          <Button
            onClick={() => refreshQR().catch(() => undefined)}
            variant="outline"
            loading={isRefreshing}
            icon={<RefreshCw size={14} />}
          >
            Gerar novo QR
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 rounded-[var(--radius-xl)] border border-[var(--border-subtle)] bg-[rgba(255,255,255,0.03)] p-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.24em] text-[var(--text-muted)]">
            Pareamento WhatsApp
          </p>
          <h3 className="mt-2 text-2xl font-semibold text-[var(--text-primary)]">
            Escaneie o QR Code
          </h3>
        </div>
        <div className="inline-flex items-center gap-2 rounded-full border border-[rgba(var(--color-brand-rgb),0.22)] bg-[rgba(var(--color-brand-rgb),0.1)] px-3 py-1 text-sm font-medium text-[var(--color-brand)]">
          <span className="status-dot status-dot-connecting" />
          {statusLabel}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
        <div className="rounded-[28px] border border-[rgba(255,255,255,0.06)] bg-[linear-gradient(160deg,rgba(255,255,255,0.08),rgba(var(--color-brand-rgb),0.08))] p-5 shadow-[0_20px_60px_rgba(8,10,34,0.35)]">
          <div className="rounded-[24px] bg-white p-5 shadow-[0_18px_40px_rgba(15,23,42,0.24)]">
            <div className="mb-4 flex items-center justify-between">
              <div className="inline-flex items-center gap-2 rounded-full bg-[rgba(var(--color-brand-rgb),0.08)] px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-[var(--color-brand)]">
                <QrCode size={14} />
                QR ativo
              </div>
              <button
                type="button"
                onClick={() => refreshQR().catch(() => undefined)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition hover:border-slate-300 hover:text-slate-900"
                aria-label="Atualizar QR Code"
              >
                {isRefreshing ? (
                  <Spinner size="sm" />
                ) : (
                  <RefreshCw size={16} />
                )}
              </button>
            </div>

            <div className="relative mx-auto flex min-h-[280px] items-center justify-center rounded-[20px] bg-[radial-gradient(circle_at_top,rgba(var(--color-brand-rgb),0.12),transparent_70%),#f8fafc] p-5">
              {qrData ? (
                <>
                  <img
                    src={qrData}
                    alt="QR Code para conectar WhatsApp"
                    width={260}
                    height={260}
                    className="block rounded-[18px]"
                  />
                  <div className="qr-scan-line" />
                </>
              ) : (
                <div className="flex h-[260px] w-[260px] flex-col items-center justify-center gap-3 rounded-[18px] border border-dashed border-slate-300 text-slate-500">
                  <Spinner size="md" />
                  <span className="text-sm">Gerando QR Code...</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded-[var(--radius-xl)] border border-[var(--border-subtle)] bg-[var(--glass-light)] p-4">
            <p className="text-xs uppercase tracking-[0.22em] text-[var(--text-muted)]">
              Como conectar
            </p>
            <ol className="mt-3 space-y-3 text-sm text-[var(--text-secondary)]">
              <li className="flex gap-3">
                <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[rgba(var(--color-brand-rgb),0.12)] text-xs font-semibold text-[var(--color-brand)]">1</span>
                Abra o WhatsApp no celular e acesse <strong className="text-[var(--text-primary)]">Aparelhos conectados</strong>.
              </li>
              <li className="flex gap-3">
                <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[rgba(var(--color-brand-rgb),0.12)] text-xs font-semibold text-[var(--color-brand)]">2</span>
                Toque em <strong className="text-[var(--text-primary)]">Conectar aparelho</strong>.
              </li>
              <li className="flex gap-3">
                <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[rgba(var(--color-brand-rgb),0.12)] text-xs font-semibold text-[var(--color-brand)]">3</span>
                Escaneie o QR ao lado e aguarde a confirmação automática.
              </li>
            </ol>
          </div>

          <div className="grid gap-3 rounded-[var(--radius-xl)] border border-[var(--border-subtle)] bg-[var(--glass-light)] p-4">
            <div className="flex items-center justify-between text-sm">
              <span className="inline-flex items-center gap-2 text-[var(--text-muted)]">
                <Smartphone size={14} />
                ID da conexão
              </span>
              <span className="font-medium text-[var(--text-primary)]">
                {remoteInstanceId ?? 'Gerando...'}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-[var(--text-muted)]">Próxima atualização</span>
              <span className="font-medium text-[var(--text-primary)]">
                {countdown}s
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-[var(--text-muted)]">Link público</span>
              <span className="font-medium text-[var(--text-primary)]">
                {publicQrUrl ? 'Ativo' : 'Desativado'}
              </span>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <Button
              variant="outline"
              icon={<RefreshCw size={14} />}
              onClick={() => refreshQR().catch(() => undefined)}
              loading={isRefreshing}
            >
              Atualizar QR
            </Button>
            <Button
              variant="ghost"
              icon={<Copy size={14} />}
              onClick={copyPublicLink}
              disabled={!publicQrUrl}
            >
              Copiar link público
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
